/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { site } from '@panel/commons/helpers';
import { ErrorMessage } from '@hookform/error-message';
import {
  Title,
  FieldRow,
  NestedRow,
  PaymentFieldSet,
  Notice,
  FieldError,
  InfoBilling,
  InfoBillingNotice
} from '../../../styles';
import { InputField, SelectField, CheckboxField } from '../fields';
import { getPaymentLoadingStatus } from '../../../../../../ducks/step4Module2';

function PaymentSection({ guessingPaymentMethod, identificationOpts, generalError }) {
  const { disablePayment } = useSelector(state => state.step4Module2);
  const loading = useSelector(getPaymentLoadingStatus);
  const { register, getValues, setValue, errors } = useFormContext();
  const { t } = useTranslation();

  const formatExpirationDate = () => {
    const value = getValues('cardExpiration');
    const dateArr = value.replace(/\s/g, '').match(/\d{1,2}/g);
    const dateResult = dateArr ? dateArr.join(' / ').substr(0, 7) : '';
    setValue('cardExpiration', dateResult);
  };

  const onlyNumberPassword = e => {
    if (e.target.type !== 'number') {
      const value = getValues(e.target.id);
      const dateArr = value.replace(/\s/g, '').match(/\d{1,4}/g);
      const result = dateArr ? dateArr.join(' ') : '';
      setValue(e.target.id, result);
    }
  };

  const onlyNumber = e => {
    if (e.type === 'keydown' && ['.', 'e', 'E', '+', '-'].includes(e.key) && e.target.type === 'number') {
      e.preventDefault();
    }
  };

  const setExpirationDate = () => {
    const date = getValues('cardExpiration');
    const dateArr = date.split('/');
    if (date && !errors.cardExpiration && dateArr.length > 1) {
      setValue('cardExpirationMonth', dateArr[0].trim());
      setValue('cardExpirationYear', dateArr[1].trim());
    }
    if (!date) {
      setValue('cardExpirationMonth', '');
      setValue('cardExpirationYear', '');
    }
  };

  return (
    <>
      <Title secondary>{t('publisher.step4.module2.payment.title')}</Title>
      <Title subtitle withNotice={site.isPeru}>
        {t('publisher.step4.module2.payment.subtitle')}
      </Title>
      {site.isPeru && (
        <InfoBillingNotice>
          <Notice blueNote>
            <span>{t('publisher.step4.module2.payment.cardNotice.question')}</span>
            {t('publisher.step4.module2.payment.cardNotice')}
            <span>{t('publisher.step4.module2.payment.cardNotice.phone')}</span>
          </Notice>
          <InfoBilling>
            <p>{t('publisher.step4.module2.payment.cardInfo.text')}</p>
          </InfoBilling>
        </InfoBillingNotice>
      )}
      <PaymentFieldSet disabled={disablePayment || loading}>
        <FieldRow>
          <InputField
            name='cardholderName'
            renderError={errorProps => (
              <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
            )}
          />
        </FieldRow>
        <FieldRow>
          <InputField
            name='email'
            renderError={errorProps => (
              <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
            )}
          />
          <NestedRow>
            <InputField
              name='areacode'
              small
              type='number'
              renderError={errorProps => (
                <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
              )}
            />
            <InputField
              name='phoneNumber'
              type='number'
              renderError={errorProps => (
                <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
              )}
            />
          </NestedRow>
        </FieldRow>
        {!site.isMexico && (
          <FieldRow>
            <SelectField
              name='docType'
              options={identificationOpts}
              renderError={errorProps => (
                <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
              )}
            />
            <InputField
              name='docNumber'
              renderError={errorProps => (
                <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
              )}
            />
          </FieldRow>
        )}
        <FieldRow>
          <InputField
            name='cardNumber'
            onChange={guessingPaymentMethod}
            renderError={errorProps => (
              <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
            )}
          />
          <NestedRow>
            <InputField
              name='cardExpiration'
              onChange={formatExpirationDate}
              onBlur={setExpirationDate}
              renderError={errorProps => (
                <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
              )}
            />
            <input
              ref={register}
              type='hidden'
              name='cardExpirationMonth'
              id='cardExpirationMonth'
              data-checkout='cardExpirationMonth'
            />
            <input
              ref={register}
              type='hidden'
              name='cardExpirationYear'
              id='cardExpirationYear'
              data-checkout='cardExpirationYear'
            />
            <InputField
              name='securityCode'
              onChange={e => onlyNumberPassword(e)}
              onKeyDown={e => onlyNumber(e)}
              type={site.isArgentina ? 'password' : 'number'}
              renderError={errorProps => (
                <ErrorMessage {...errorProps} render={({ message }) => <FieldError>{t(message)}</FieldError>} />
              )}
            />
          </NestedRow>
        </FieldRow>
        <FieldRow>
          <CheckboxField
            name='billingTos'
            isTerms
            renderText={() => (
              <>
                {t('publisher.step4.module2.terms.use.label')}
                <a target='_blank' rel='noopener noreferrer' href={t('publisher.step4.module2.terms.use.href')}>
                  {t('publisher.step4.module2.terms.use')}
                </a>
                {t('publisher.step4.module2.terms.separator')}
                <a target='_blank' rel='noopener noreferrer' href={t('publisher.step4.module2.terms.hiring.href')}>
                  {t('publisher.step4.module2.terms.hiring')}
                </a>
              </>
            )}
            renderError={errorProps => (
              <ErrorMessage
                {...errorProps}
                render={({ message }) => <FieldError isCheckbox>{t(message)}</FieldError>}
              />
            )}
          />
        </FieldRow>
      </PaymentFieldSet>
      <Notice footNote>
        {t('publisher.step4.module2.payment.legalNotice')}
        <a target='_blank' rel='noopener noreferrer' href={t('publisher.step4.module2.terms.hiring.href')}>
          {site.id === 'UBPE' ? t('publisher.step4.module2.terms.hiring2') : t('publisher.step4.module2.terms.use')}
        </a>
      </Notice>
      {generalError && <FieldError generalError>{generalError}</FieldError>}
    </>
  );
}

export default PaymentSection;
